import React, { useState, useEffect } from 'react';
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Copy, RefreshCw, CheckCircle2, AlertTriangle, Key, ChevronDown, ChevronUp, Settings2, Clock } from 'lucide-react';
import { notify } from '@/components/core';

interface WebhookPanelProps {
    data: any;
    onChange: (key: string, value: any) => void;
    flowId: string;
}

export const WebhookPanel = ({ data, onChange, flowId }: WebhookPanelProps) => {
    const [listening, setListening] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Auto-generate URL
    const baseUrl = API_BASE_URL.endsWith('/api/v1') ? API_BASE_URL.replace(/\/api\/v1$/, '') : API_BASE_URL;
    const webhookUrl = `${baseUrl}/api/v1/flows/${flowId}/trigger`;
    
    const isNewFlow = flowId === 'new' || !flowId;

    const authType = data.authType || 'shared_secret';
    const authToken = data.authToken || '';

    const isTokenExpiredWithNewDuration = (generatedAtStr: string, durationStr: string) => {
        if (!generatedAtStr || !durationStr || durationStr === 'never') return false;
        try {
            const generatedAt = new Date(generatedAtStr).getTime();
            if (isNaN(generatedAt)) return false;
            
            const now = Date.now();
            let durationMs = 0;
            switch (durationStr) {
                case '1h': durationMs = 60 * 60 * 1000; break;
                case '24h': durationMs = 24 * 60 * 60 * 1000; break;
                case '7d': durationMs = 7 * 24 * 60 * 60 * 1000; break;
                case '30d': durationMs = 30 * 24 * 60 * 60 * 1000; break;
                default: return false;
            }
            return now > (generatedAt + durationMs);
        } catch (e) {
            return false;
        }
    };
    
    const willExpireImmediately = isTokenExpiredWithNewDuration(data.tokenGeneratedAt, data.tokenExpiryDuration);

    const getExpirationDateStr = (generatedAtStr: string, durationStr: string) => {
        if (!generatedAtStr || !durationStr || durationStr === 'never') return '';
        try {
            const generatedAt = new Date(generatedAtStr).getTime();
            if (isNaN(generatedAt)) return '';
            
            let durationMs = 0;
            switch (durationStr) {
                case '1h': durationMs = 60 * 60 * 1000; break;
                case '24h': durationMs = 24 * 60 * 60 * 1000; break;
                case '7d': durationMs = 7 * 24 * 60 * 60 * 1000; break;
                case '30d': durationMs = 30 * 24 * 60 * 60 * 1000; break;
                default: return '';
            }
            return new Date(generatedAt + durationMs).toLocaleString();
        } catch (e) {
            return '';
        }
    };

    // BUG-13: Generate 256-bit (64 hex char) token instead of 128-bit UUID
    const generateToken = () => {
        const arr = new Uint8Array(32);
        crypto.getRandomValues(arr);
        return Array.from(arr).map(b => b.toString(16).padStart(2, '0')).join('');
    };

    useEffect(() => {
        if (!authToken && (authType === 'shared_secret' || authType === 'bearer')) {
            onChange('authToken', generateToken());
            setTimeout(() => onChange('tokenGeneratedAt', new Date().toISOString()), 50);
        }
    }, [authType, authToken, onChange]);

    const handleAuthTypeChange = (value: string) => {
        onChange('authType', value);
        
        if (value === 'public') {
            // Clear token and related metadata when switching to public.
            // Using staggered timeouts to prevent React state batching race conditions in the parent.
            setTimeout(() => onChange('authToken', ''), 20);
            setTimeout(() => onChange('tokenGeneratedAt', ''), 40);
            setTimeout(() => onChange('tokenExpiryDuration', 'never'), 60);
        }
    };

    const copyUrl = () => {
        navigator.clipboard.writeText(webhookUrl);
        notify.success("Webhook URL copied to clipboard");
    };
    
    const copySecret = () => {
        navigator.clipboard.writeText(authToken);
        notify.success("Secret copied to clipboard");
    };

    // BUG-09: Real polling — poll the last-received event from the backend
    const startListening = async () => {
        setListening(true);
        notify.info("Listening for events... Trigger your webhook now!");

        const deadline = Date.now() + 60_000; // 60s timeout
        const poll = async () => {
            if (Date.now() > deadline) {
                setListening(false);
                notify.error("Timed out. No event received within 60 seconds.");
                return;
            }
            try {
                const res = await fetch(`${(import.meta.env.VITE_API_BASE_URL || '').replace(/\/api\/v1$/, '')}/api/v1/flows/${flowId}/trigger/last-event`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}` }
                });
                if (res.ok) {
                    const json = await res.json();
                    if (json?.payload) {
                        setListening(false);
                        onChange('hasSchema', true);
                        onChange('detectedSchema', json.payload);
                        notify.success("Event received! Schema automatically detected.");
                        return;
                    }
                }
            } catch (_) { /* network hiccup — keep polling */ }
            setTimeout(poll, 2000); // poll every 2s
        };
        setTimeout(poll, 2000);
    };

    return (
        <div className="space-y-6">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                        <h4 className="font-semibold text-yellow-900 text-sm">Webhook Trigger</h4>
                        <p className="text-xs text-yellow-700 mt-1">
                            This node automatically creates an endpoint for your flow.
                            Send any JSON payload to the URL below to trigger this workflow.
                        </p>
                    </div>
                </div>
            </div>

            <div className={`space-y-2 ${isNewFlow ? 'opacity-50 pointer-events-none' : ''}`}>
                <Label>HTTP Method</Label>
                <select
                    className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    value={data.method || 'POST'}
                    onChange={e => onChange('method', e.target.value)}
                >
                    <option value="POST">POST (Recommended)</option>
                    <option value="GET">GET</option>
                    <option value="PUT">PUT</option>
                </select>
            </div>

            <div className="space-y-2">
                <Label>Webhook URL</Label>
                {isNewFlow ? (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-500 text-center italic">
                        Please save this flow to generate your unique Webhook URL.
                    </div>
                ) : (
                    <div className="flex gap-2">
                        <Input value={webhookUrl} readOnly className="bg-gray-50 font-mono text-xs" />
                        <Button variant="outline" size="icon" onClick={copyUrl} title="Copy URL">
                            <Copy className="w-4 h-4" />
                        </Button>
                    </div>
                )}
            </div>
            
            <div className={`space-y-4 border-t pt-4 ${isNewFlow ? 'opacity-50 pointer-events-none' : ''}`}>
                <Label className="block">Authentication</Label>
                <RadioGroup value={authType} onValueChange={handleAuthTypeChange} className="space-y-2">
                    <div className="flex items-center space-x-2">
                        <RadioGroupItem value="shared_secret" id="shared_secret" />
                        <Label htmlFor="shared_secret" className="font-normal cursor-pointer">
                            Shared Secret <span className="text-gray-400 text-xs">(Recommended)</span>
                        </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                        <RadioGroupItem value="bearer" id="bearer" />
                        <Label htmlFor="bearer" className="font-normal cursor-pointer">
                            Bearer Token
                        </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                        <RadioGroupItem value="public" id="public" />
                        <Label htmlFor="public" className="font-normal cursor-pointer text-red-600">
                            Public <span className="text-red-400 text-xs">(No Authentication)</span>
                        </Label>
                    </div>
                </RadioGroup>

                {authType === 'public' && (
                    <div className="flex items-start gap-2 bg-red-50 p-3 rounded-md border border-red-200 text-red-700 mt-2">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <p className="text-xs">
                            <strong>Warning:</strong> This webhook is completely public. Anyone with the URL can trigger this flow and insert data into your system.
                        </p>
                    </div>
                )}

                {authType !== 'public' && authToken && (
                    <div className="space-y-2 mt-3 p-3 bg-gray-50 rounded-md border">
                        {/* BUG-12: Show correct label based on auth type */}
                        <Label className="text-xs text-gray-500">
                            {authType === 'shared_secret' ? 'Shared Secret' : 'Bearer Token'}
                        </Label>
                        <div className="flex gap-2">
                            <Input value={authToken} readOnly type="password" onClick={(e) => (e.target as HTMLInputElement).type = 'text'} onBlur={(e) => (e.target as HTMLInputElement).type = 'password'} className="font-mono text-xs cursor-pointer" />
                            <Button variant="outline" size="icon" onClick={copySecret} title="Copy Secret">
                                <Copy className="w-4 h-4" />
                            </Button>
                            <Button variant="outline" size="icon" onClick={() => {
                                onChange('authToken', generateToken());
                                setTimeout(() => onChange('tokenGeneratedAt', new Date().toISOString()), 50);
                            }} title="Regenerate Secret">
                                <RefreshCw className="w-4 h-4" />
                            </Button>
                        </div>
                        <p className="text-[10px] text-gray-400">
                            {authType === 'shared_secret' 
                                ? "Pass this via X-AIDAS-Webhook-Secret header." 
                                : "Pass this via Authorization: Bearer header."}
                        </p>
                        
                        {willExpireImmediately ? (
                            <div className="mt-2 text-xs text-red-600 flex items-center gap-1 font-medium bg-red-50 p-2 rounded border border-red-100">
                                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                <span>Token is expired. Webhook will fail until regenerated.</span>
                            </div>
                        ) : (
                            data.tokenExpiryDuration && data.tokenExpiryDuration !== 'never' && (
                                <div className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                                    <Clock className="w-3 h-3 shrink-0" />
                                    <span>
                                        Expires {data.tokenExpiryDuration} after generation
                                        {data.tokenGeneratedAt ? ` (${getExpirationDateStr(data.tokenGeneratedAt, data.tokenExpiryDuration)})` : ''}.
                                    </span>
                                </div>
                            )
                        )}
                    </div>
                )}
            </div>

            <div className={`border-t pt-4 ${isNewFlow ? 'opacity-50 pointer-events-none' : ''}`}>
                <Label className="mb-2 block">Schema Discovery</Label>
                {data.hasSchema ? (
                    <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-md text-sm border border-green-200">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Schema Detected & Ready</span>
                        <Button variant="ghost" size="sm" className="ml-auto h-6 text-xs" onClick={() => onChange('hasSchema', false)}>
                            Reset
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-xs text-gray-500">
                            We need to know what your data looks like. Click "Listen" and then trigger your webhook to auto-generate the schema.
                        </p>
                        <Button
                            className="w-full"
                            variant={listening ? "secondary" : "default"}
                            onClick={startListening}
                            disabled={listening || isNewFlow}
                        >
                            {listening ? (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    Listening for Event...
                                </>
                            ) : (
                                "Listen for Event"
                            )}
                        </Button>
                    </div>
                )}
            </div>

            <div className={`border-t pt-4 ${isNewFlow ? 'opacity-50 pointer-events-none' : ''}`}>
                <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex w-full items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
                >
                    <div className="flex items-center gap-2">
                        <Settings2 className="w-4 h-4" />
                        Advanced Settings
                    </div>
                    {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                
                {showAdvanced && (
                    <div className="mt-4 space-y-4">
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-500" />
                                Token Expiration
                            </Label>
                            <select
                                className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                                value={data.tokenExpiryDuration || 'never'}
                                onChange={e => {
                                    onChange('tokenExpiryDuration', e.target.value);
                                    if (!data.tokenGeneratedAt) {
                                        setTimeout(() => onChange('tokenGeneratedAt', new Date().toISOString()), 50);
                                    }
                                }}
                            >
                                <option value="never">Never (Default)</option>
                                <option value="1h">1 Hour</option>
                                <option value="24h">24 Hours</option>
                                <option value="7d">7 Days</option>
                                <option value="30d">30 Days</option>
                            </select>
                            
                            {willExpireImmediately && (
                                <div className="flex flex-col gap-2 bg-amber-50 p-3 rounded-md border border-amber-200 mt-2 text-amber-800">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                        <p className="text-xs">
                                            <strong>Warning:</strong> The current token has already exceeded the selected expiry period. It will expire immediately after saving, breaking any current integrations.
                                        </p>
                                    </div>
                                    <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="self-start mt-1 bg-white hover:bg-amber-100 border-amber-300 text-amber-700 h-8"
                                        onClick={() => {
                                            onChange('authToken', generateToken());
                                            setTimeout(() => onChange('tokenGeneratedAt', new Date().toISOString()), 50);
                                        }}
                                    >
                                        <RefreshCw className="w-3 h-3 mr-2" />
                                        Regenerate Token Now (Recommended)
                                    </Button>
                                </div>
                            )}

                            <p className="text-xs text-gray-500">
                                Automatically expire this webhook token after the specified duration. The duration starts from when the token was last generated.
                            </p>
                            {data.tokenGeneratedAt && (
                                <p className="text-[10px] text-gray-400 mt-1">
                                    Last generated: {new Date(data.tokenGeneratedAt).toLocaleString()}
                                </p>
                            )}
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};
